import { useMemo } from 'react';
import { useAuthStore } from '@/store/authStore';

// Route definitions with school slug prefix
export const ROUTES = {
  // Public routes (no school slug)
  login: () => '/auth/login',
  signup: () => '/auth/signup',
  /**
   * Creating another school. Outside the `/:schoolSlug` tree deliberately: the
   * school it is about does not exist yet, and the page is also reached signed
   * out, on the way back from the provider.
   */
  newSchool: () => '/auth/new-school',

  // Protected routes (with school slug)
  dashboard: (slug: string) => `/${slug}/dashboard`,
  gradebook: (slug: string) => `/${slug}/gradebook`,
  students: (slug: string) => `/${slug}/students`,
  studentDetail: (slug: string, id: string) => `/${slug}/students/${id}`,
  courses: (slug: string) => `/${slug}/courses`,
  courseDetail: (slug: string, id: string) => `/${slug}/courses/${id}`,
  calendar: (slug: string) => `/${slug}/calendar`,
  myGrades: (slug: string) => `/${slug}/my-grades`,
  // The year-end subject rollup (AR-12). Three entry points, one page: a student's
  // own record, a parent's child, and a staff view of a named student.
  myYearEndGrades: (slug: string) => `/${slug}/year-end-grades`,
  studentYearEndGrades: (slug: string, id: string) =>
    `/${slug}/students/${id}/year-end-grades`,
  parent: (slug: string) => `/${slug}/parent`,
  childGrades: (slug: string, studentId: string) =>
    `/${slug}/parent/children/${studentId}/grades`,
  childYearEndGrades: (slug: string, studentId: string) =>
    `/${slug}/parent/children/${studentId}/year-end-grades`,
  settings: (slug: string) => `/${slug}/settings`,
  /**
   * The signed-in person's own sign-in settings, which is why it sits under
   * `account/` rather than in `settings/`: everything there is the school's and
   * admin-only, and this is one person's own and open to all four roles. Still
   * inside the `/:schoolSlug` tree, because the shell around it is.
   */
  accountSecurity: (slug: string) => `/${slug}/account/security`,
  /** The app's release notes. Open to every role, like `accountSecurity`. */
  whatsNew: (slug: string) => `/${slug}/whats-new`,
  mySubmissions: (slug: string) => `/${slug}/my-submissions`,
  myGradeChanges: (slug: string) => `/${slug}/my-grade-changes`,
  bulkImport: (slug: string) => `/${slug}/import`,
  // Enrolment forms, read side by side against the record they become. Its own
  // route rather than a tab of the spreadsheet importer: a packet is reviewed
  // one child at a time against a document, and a CSV is reviewed as a grid.
  studentIntake: (slug: string) => `/${slug}/import/enrolment-forms`,
  // Attendance (design 1a). Homeroom-and-day scoped, so it sits beside the
  // course-scoped gradebook rather than inside it. The section root is a way in
  // rather than a page: it sends an admin to the overview and everyone else to
  // their register, so the one entry point works for both.
  attendance: (slug: string) => `/${slug}/attendance`,
  // One homeroom's register for one day.
  attendanceToday: (slug: string) => `/${slug}/attendance/today`,
  // The office's day (1b): every late drop-off and early pickup in the school,
  // which is why it is not scoped to a homeroom the way the register is. It
  // leads the section — it is the page the office opens attendance for.
  attendanceOverview: (slug: string) => `/${slug}/attendance/overview`,
  // The pastoral groups the register is taken for, so they sit inside
  // attendance rather than in the admin section. Editing one stays admin-only —
  // a teacher taking a register must not be able to dissolve a group — but that
  // is a role check on the route, not a reason to file the page elsewhere.
  attendanceHomerooms: (slug: string) => `/${slug}/attendance/homerooms`,
  // Totals over a term or a year: the whole school for the office, or one
  // homeroom for whoever may take its register.
  attendanceReports: (slug: string) => `/${slug}/attendance/reports`,
  // Admin routes
  adminApprovals: (slug: string) => `/${slug}/admin/approvals`,
  adminGradeChanges: (slug: string) => `/${slug}/admin/grade-changes`,
  adminPeople: (slug: string) => `/${slug}/admin/people`,
  adminParents: (slug: string) => `/${slug}/admin/parents`,
  adminTeachers: (slug: string) => `/${slug}/admin/teachers`,
  adminDepartments: (slug: string) => `/${slug}/admin/departments`,
  adminAcademicYears: (slug: string) => `/${slug}/admin/academic-years`,
  // A year and its terms are set up on one page, so "new" and "edit" are the
  // same screen with and without an id.
  adminAcademicYearNew: (slug: string) => `/${slug}/admin/academic-years/new`,
  adminAcademicYearSetup: (slug: string, yearId: string) =>
    `/${slug}/admin/academic-years/${yearId}`,
  adminAuditLog: (slug: string) => `/${slug}/admin/audit-log`,
  adminGradePosting: (slug: string) => `/${slug}/admin/grade-posting`,
} as const;

/**
 * Routes for the school in scope.
 *
 * Memoised on the slug, so the object is the same one from render to render.
 * It is not a convenience: callers put `routes` in a `useMemo`/`useEffect`
 * dependency list, and a fresh object every render quietly turns those into no
 * memo at all — which is how a re-render reaches things that recompute on
 * identity, like the roster's columns or a form's `defaultValues`.
 */
export function useSchoolRoutes() {
  const user = useAuthStore((state) => state.user);
  const slug = user?.schoolSlug ?? '';

  return useMemo(() => ({
    slug,
    dashboard: () => ROUTES.dashboard(slug),
    gradebook: () => ROUTES.gradebook(slug),
    students: () => ROUTES.students(slug),
    studentDetail: (id: string) => ROUTES.studentDetail(slug, id),
    courses: () => ROUTES.courses(slug),
    courseDetail: (id: string) => ROUTES.courseDetail(slug, id),
    calendar: () => ROUTES.calendar(slug),
    myGrades: () => ROUTES.myGrades(slug),
    myYearEndGrades: () => ROUTES.myYearEndGrades(slug),
    studentYearEndGrades: (id: string) => ROUTES.studentYearEndGrades(slug, id),
    parent: () => ROUTES.parent(slug),
    childGrades: (studentId: string) => ROUTES.childGrades(slug, studentId),
    childYearEndGrades: (studentId: string) =>
      ROUTES.childYearEndGrades(slug, studentId),
    settings: () => ROUTES.settings(slug),
    accountSecurity: () => ROUTES.accountSecurity(slug),
    whatsNew: () => ROUTES.whatsNew(slug),
    mySubmissions: () => ROUTES.mySubmissions(slug),
    myGradeChanges: () => ROUTES.myGradeChanges(slug),
    bulkImport: () => ROUTES.bulkImport(slug),
    studentIntake: () => ROUTES.studentIntake(slug),
    attendance: () => ROUTES.attendance(slug),
    attendanceToday: () => ROUTES.attendanceToday(slug),
    attendanceOverview: () => ROUTES.attendanceOverview(slug),
    attendanceHomerooms: () => ROUTES.attendanceHomerooms(slug),
    attendanceReports: () => ROUTES.attendanceReports(slug),
    adminApprovals: () => ROUTES.adminApprovals(slug),
    adminGradeChanges: () => ROUTES.adminGradeChanges(slug),
    adminPeople: () => ROUTES.adminPeople(slug),
    adminParents: () => ROUTES.adminParents(slug),
    adminTeachers: () => ROUTES.adminTeachers(slug),
    adminDepartments: () => ROUTES.adminDepartments(slug),
    adminAcademicYears: () => ROUTES.adminAcademicYears(slug),
    adminAcademicYearNew: () => ROUTES.adminAcademicYearNew(slug),
    adminAcademicYearSetup: (yearId: string) =>
      ROUTES.adminAcademicYearSetup(slug, yearId),
    adminAuditLog: () => ROUTES.adminAuditLog(slug),
    adminGradePosting: () => ROUTES.adminGradePosting(slug),
    // Login is public, no slug needed
    login: () => ROUTES.login(),
  }), [slug]);
}
